// Source unique de vérité pour l'affichage des statuts et des étapes du workflow.
// Aucune icône / émoji : uniquement des classes de couleur Tailwind.
// Les classes sont écrites en toutes lettres pour ne pas être purgées par Tailwind.

// Badge par étape (currentStage)
const STAGE_BADGE = {
  marketing:        { label: 'En attente Marketing',   classes: 'bg-indigo-100 text-indigo-800' },
  marketing_approbation: { label: 'En attente Signataire', classes: 'bg-purple-100 text-purple-800' },
  force_de_vente:   { label: 'En attente Animateur',   classes: 'bg-blue-100 text-blue-800' },
  distributeur:     { label: 'En attente Distributeur', classes: 'bg-teal-100 text-teal-800' },
  chef_de_zone:     { label: 'En attente Chef de Zone', classes: 'bg-orange-100 text-orange-800' },
  marketing_review: { label: 'En revue Marketing',     classes: 'bg-violet-100 text-violet-800' },
  directrice:       { label: 'En attente Directrice',  classes: 'bg-amber-100 text-amber-800' },
  commercial:       { label: 'En attente Commercial',  classes: 'bg-cyan-100 text-cyan-800' },
  completed:        { label: 'Complétée',              classes: 'bg-green-100 text-green-800' },
};

// Libellé court par étape (frise + libellé des refus)
export const STAGE_LABEL_SHORT = {
  marketing: 'Marketing',
  marketing_approbation: 'Approbation signataire',
  force_de_vente: 'Animateur',
  distributeur: 'Distributeur',
  chef_de_zone: 'Chef de Zone',
  marketing_review: 'Revue Marketing',
  directrice: 'Directrice',
  commercial: 'Commercial',
  completed: 'Complétée',
};

// Couleur de la pastille d'étape « en cours » dans la frise (une couleur par étape)
export const STAGE_DOT = {
  marketing: 'bg-indigo-500',
  marketing_approbation: 'bg-purple-500',
  force_de_vente: 'bg-blue-500',
  distributeur: 'bg-teal-500',
  chef_de_zone: 'bg-orange-500',
  marketing_review: 'bg-violet-500',
  directrice: 'bg-amber-500',
  commercial: 'bg-cyan-500',
  completed: 'bg-green-500',
};

/**
 * Renvoie le libellé + les classes Tailwind du badge de statut.
 * @param {string} status - Request.status
 * @param {string} currentStage - Request.currentStage
 * @param {string} [fdvValidationStatus] - statut de la validation FDV ('processing' = prise en charge en cours)
 * @returns {{label: string, classes: string}}
 */
export function getStatusBadge(status, currentStage, fdvValidationStatus) {
  if (status === 'cancelled') {
    return { label: 'Annulée', classes: 'bg-gray-100 text-gray-800' };
  }
  if (status === 'completed' || currentStage === 'completed') {
    return STAGE_BADGE.completed;
  }
  if (status && status.includes('rejected')) {
    const stageKey = status.replace('_rejected', '');
    const short = STAGE_LABEL_SHORT[stageKey] || STAGE_LABEL_SHORT[currentStage] || '';
    return { label: short ? `Refusée — ${short}` : 'Refusée', classes: 'bg-red-100 text-red-800' };
  }
  // Animateur a pris en charge : signatures en cours de collecte
  if (currentStage === 'force_de_vente' && fdvValidationStatus === 'processing') {
    return { label: 'En cours de signature', classes: 'bg-blue-100 text-blue-800' };
  }
  return STAGE_BADGE[currentStage] || { label: 'En attente', classes: 'bg-gray-100 text-gray-800' };
}

/**
 * Renvoie la séquence ordonnée des étapes du workflow (frise), terminée par 'completed'.
 * @param {string} workflowType - request.workflowType || request.requestType
 * @param {string} [sourceCategory] - catégorie de la source ('internal' | 'distributor')
 * @param {string} [circuitTerrain] - request.circuitTerrain : demande créée par un animateur
 *   ou un chef de zone, sans étape marketing (et sans visa du chef de zone s'il l'a créée)
 * @returns {Array<{key: string, label: string}>}
 */
export function getWorkflowSteps(workflowType, sourceCategory, requestType, currentStage, circuitTerrain) {
  let stages;
  switch (workflowType) {
    case 'business_unit':
      stages = ['directrice'];
      break;
    case 'decharge_animateur':
      // Toujours workflow court : pas d'étape marketing initiale, quel que soit le motif.
      stages = ['force_de_vente', 'chef_de_zone', 'distributeur', 'marketing_review', 'directrice', 'commercial'];
      break;
    default:
      stages = sourceCategory === 'internal'
        ? ['marketing', 'directrice']
        : ['marketing', 'force_de_vente', 'chef_de_zone', 'distributeur', 'marketing_review', 'directrice', 'commercial'];
  }
  if (circuitTerrain) {
    stages = stages.filter((s) => s !== 'marketing' && !(circuitTerrain === 'chef_de_zone' && s === 'chef_de_zone'));
  }
  // L'approbation signataire n'apparaît que lorsqu'elle bloque réellement la demande
  // (validation posée par un marketeur non signataire).
  if (currentStage === 'marketing_approbation' && stages.includes('marketing')) {
    stages = stages.flatMap((s) => (s === 'marketing' ? [s, 'marketing_approbation'] : [s]));
  }
  return [...stages, 'completed'].map((key) => ({ key, label: STAGE_LABEL_SHORT[key] }));
}

/**
 * État d'une étape donnée dans la frise.
 * @returns {'done'|'current'|'rejected'|'upcoming'}
 */
export function getStepState(stepKey, steps, currentStage, status) {
  const rejected = !!(status && status.includes('rejected'));

  if (status === 'completed' || currentStage === 'completed') {
    return stepKey === 'completed' ? 'current' : 'done';
  }

  const currentIndex = steps.findIndex((s) => s.key === currentStage);
  const stepIndex = steps.findIndex((s) => s.key === stepKey);

  if (currentIndex === -1) return 'upcoming';
  if (stepIndex < currentIndex) return 'done';
  if (stepIndex === currentIndex) return rejected ? 'rejected' : 'current';
  return 'upcoming';
}
